import React, { createContext, useEffect, useMemo, useState } from "react";

//api
import { getAllUsers, getOrganisationRoleForUser, getWorkspacesForUser } from  "@/api/orgSettings/users";

//interfaces
import { NotificationStatus } from  "@/interfaces/languages";
import { PaginationSettings } from  "@/interfaces/lists";
import { AllUsersSortByType, IUserData, ResourcePermission } from  "@/interfaces/orgSettings";
import { SortBy } from  "@/interfaces/toggle";
import { IUserWorkspaceData, WorkspaceSortByType } from  "@/interfaces/workspaces";



export interface IOrgSettingsUsersContext {
    userId: string,
    setUserId: (userId: string) => void,
    userRole: ResourcePermission,
    getUsers: any;
    userData: IUserData[],
    notificationDetails: {show: boolean, status: NotificationStatus, message: string},
    setNotificationDetails:any,
    paginationSettings: PaginationSettings,
    workspacePaginationSettings: PaginationSettings,
    sortToggle: [AllUsersSortByType, SortBy],
    sortWorkspacesToggle:  [WorkspaceSortByType, SortBy],
    setSortToggle: ([AllUsersSortByType, SortBy]) => void,
    setSortWorkspacesToggle: ([WorkspaceSortByType, SortBy]) => void,
    searchString: string,
    setSearchString: (search: string) => void,
    getUserPermission: any,
    includeArchivedUsers: boolean,
    setIncludeArchivedUsers: (include: boolean) => void,
    workspaceData: IUserWorkspaceData[],
    setIsLoading: (loading: boolean) => void,
    isLoading: boolean,
    getUsersWorkspaces: any,
}

export const orgSettingsUsersDefaults:IOrgSettingsUsersContext = {
    userId: undefined,
    userRole: undefined,
    setUserId:() => {},
    getUsers: () => {},
    userData: undefined,
    notificationDetails: {show: false, status: null, message: ""},
    setNotificationDetails:() => {},
    paginationSettings: {hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0},
    workspacePaginationSettings: {hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0},
    sortToggle: [AllUsersSortByType.PERMISSIONS, SortBy.DESCENDING],
    sortWorkspacesToggle: [WorkspaceSortByType.WORKSPACE, SortBy.ASCENDING],
    setSortToggle: () => {},
    setSortWorkspacesToggle: () => {},
    searchString: "",
    setSearchString: () => {},
    getUserPermission: () => {},
    includeArchivedUsers: undefined,
    setIncludeArchivedUsers:() => {},
    workspaceData: undefined,
    isLoading:true,
    setIsLoading:() => {},
    getUsersWorkspaces:() => {}
}

export const OrgSettingsUsersContext = createContext(orgSettingsUsersDefaults);

const OrgSettingsUsersProvider = ({children}:{children:React.ReactNode}) => {
    const top : number = 20;  
    const [userId, setUserId] = useState<string>("");
    const [userData, setUserData] = useState<IUserData[]>(undefined);
    const [notificationDetails, setNotificationDetails] = useState<{show: boolean, status: NotificationStatus, message: string}>({show: false, status: null, message: ""});
    const [includeArchivedUsers, setIncludeArchivedUsers] = useState<boolean>(false);
    const [searchString, setSearchString] = useState<string>("");
    const [sortToggle, setSortToggle] = useState<[AllUsersSortByType, SortBy]>([AllUsersSortByType.PERMISSIONS, SortBy.DESCENDING]);
    const [sortWorkspacesToggle, setSortWorkspacesToggle] = useState<[WorkspaceSortByType, SortBy]>([WorkspaceSortByType.WORKSPACE, SortBy.ASCENDING]);
    const [paginationSettings, setPaginationSettings] = useState<PaginationSettings>({hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0});
    const [workspacePaginationSettings, setWorkspacePaginationSettings] = useState<PaginationSettings>({hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0});
    const [userRole, setUserRole] = useState<ResourcePermission>(undefined);
    const [workspaceData, setWorkspaceData] = useState<IUserWorkspaceData[]>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [abortController, setAbortController] = useState<AbortController>(null);

    useEffect(() => {
        if (notificationDetails.show) {
            const timer = setTimeout(() => {
                setNotificationDetails({show: false, status:null, message: ""});
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [notificationDetails]);
  
    function getUserPermission() {
        getOrganisationRoleForUser(userId)
        .then((data) => {
            setUserRole(data === "manage" ? ResourcePermission.MANAGE : ResourcePermission.READ);
        })
    }

    function getUsers(page?: number){
        if (abortController) { abortController.abort(); }
    
        const newAbortController = new AbortController();
        setAbortController(newAbortController);
        setIsLoading(true);

        let skip = 0;
        if (page) { skip = ((page - 1) * top);} 

        let params = {
            filters: includeArchivedUsers ? ['includeInactive'] : [],
            searchTerm: searchString,
            sortBy: sortToggle[0],
            sort: sortToggle[1],
            top: top,
            skip: skip
        };

        getAllUsers(params, { signal: newAbortController.signal })
        .then((data) =>   {
            setUserData(data.records);
            setPaginationSettings({
                hasPagination: data.total > data.top,
                currentPage:  Math.ceil(page ?? 1),
                totalPages: Math.ceil(data.total / data.top),
                pageSize: data.top,
                totalItems: data.total
            })
        })
        .catch((error) => {
            if (error.name !== "AbortError" && error.code !== "ERR_CANCELED") {
                setNotificationDetails({ show: true, status: NotificationStatus.error, message: "Unable to get user data, please refresh the page."});
            }
        })
    }

    function getUsersWorkspaces(page?: number){  
        setIsLoading(true);
        if (!userId){
            return
        }
        let skip = 0;
        if (page) { skip = ((page - 1) * top);} 
  
        let params = {
            sortBy: sortWorkspacesToggle[0],
            sort: sortWorkspacesToggle[1],
            top: top,
            skip: skip
        };

        getWorkspacesForUser(userId, params)
        .then((data) =>   {
            setWorkspaceData([...data.records]);
            setWorkspacePaginationSettings({
                hasPagination: data.total > data.top,
                currentPage:  Math.ceil(page ?? 1),
                totalPages: Math.ceil(data.total / data.top),
                pageSize: data.top,
                totalItems: data.total
            })
        })
        .catch(() => {
            setNotificationDetails({ show: true, status: NotificationStatus.error, message: "Unable to get workspaces for user."});
        })
    }

    const context = useMemo(() => (
        {userId, setUserId, notificationDetails, setNotificationDetails, getUsers, userData, workspaceData, paginationSettings, searchString, setSearchString, sortToggle, setSortToggle, userRole, getUserPermission, includeArchivedUsers, setIncludeArchivedUsers, setSortWorkspacesToggle, sortWorkspacesToggle, isLoading, setIsLoading, workspacePaginationSettings, getUsersWorkspaces}
    ), [userId, notificationDetails, sortWorkspacesToggle, isLoading, userData, sortToggle, searchString, paginationSettings, userRole, includeArchivedUsers, workspaceData, workspacePaginationSettings]);
      
    return (
      <OrgSettingsUsersContext.Provider value={context}>
        {children}
      </OrgSettingsUsersContext.Provider>
    );
};

export default OrgSettingsUsersProvider;